'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, FileText, Cloud } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b">
        <div className="container max-w-4xl mx-auto px-4 py-4">
          <Link href="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="container max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <FileText className="w-8 h-8 text-blue-600" />
          <h1 className="text-4xl font-bold">Terms of Service</h1>
        </div>

        <p className="text-muted-foreground mb-8">
          Last updated: January 2025
        </p>

        <div className="prose dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground">
              By accessing or using Apprendo services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground">
              Apprendo provides a platform for capturing, processing, and sharing knowledge from team conversations. Our services include:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
              <li>Video recording and transcription</li>
              <li>AI-powered moment detection</li>
              <li>Content management and sharing tools</li>
              <li>Integration with third-party platforms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. User Accounts</h2>
            <p className="text-muted-foreground">
              You are responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Providing accurate and complete information</li>
              <li>Notifying us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Acceptable Use</h2>
            <p className="text-muted-foreground">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
              <li>Use the service for any unlawful purpose</li>
              <li>Upload malicious code or harmful content</li>
              <li>Violate intellectual property rights</li>
              <li>Harass, abuse, or harm others</li>
              <li>Attempt to gain unauthorized access</li>
              <li>Interfere with service operation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Content Ownership</h2>
            <h3 className="text-xl font-semibold mb-2">Your Content</h3>
            <p className="text-muted-foreground">
              You retain all rights to content you upload. By using our service, you grant Apprendo a license to process, analyze, and store your content solely to provide our services.
            </p>

            <h3 className="text-xl font-semibold mt-4 mb-2">Our Content</h3>
            <p className="text-muted-foreground">
              Apprendo retains all rights to our platform, technology, and any insights or improvements derived from aggregate, anonymized data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Privacy and Data Protection</h2>
            <p className="text-muted-foreground">
              Your use of our services is also governed by our Privacy Policy. We are committed to protecting your data with enterprise-grade security measures.
            </p>
          </section>

          <Card className="p-6 my-8 border-2 border-green-200 dark:border-green-900/50 bg-green-50/50 dark:bg-green-950/20">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Cloud className="w-6 h-6 text-green-600 dark:text-green-400" />
              Enterprise Private Cloud Terms
            </h2>
            <p className="text-muted-foreground mb-4">
              Enterprise customers may opt for private cloud deployment with the following additional terms:
            </p>
            <div className="space-y-3">
              <div>
                <h3 className="font-semibold mb-1">Data Ownership & Storage</h3>
                <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                  <li>You retain complete ownership of all data stored in your designated cloud infrastructure</li>
                  <li>Videos, transcripts, and moments are stored exclusively in your AWS S3, Azure Blob Storage, or Google Cloud Storage</li>
                  <li>Apprendo processes data within your infrastructure without creating external copies</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-1">Infrastructure Requirements</h3>
                <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                  <li>You are responsible for maintaining your cloud infrastructure and associated costs</li>
                  <li>Minimum technical requirements must be met for proper service operation</li>
                  <li>You grant Apprendo necessary permissions to process data within your designated environment</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-1">Compliance & Security</h3>
                <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                  <li>Your existing cloud compliance certifications (HIPAA, GDPR, etc.) apply to your data</li>
                  <li>Apprendo's processing adheres to your security policies and access controls</li>
                  <li>Audit logs remain within your infrastructure for complete transparency</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-1">Service Level Agreement</h3>
                <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                  <li>Enterprise SLA terms supersede standard terms where applicable</li>
                  <li>Custom data retention policies can be configured</li>
                  <li>Dedicated support channel for infrastructure-related issues</li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4 italic">
              Enterprise Private Cloud deployment requires a separate Enterprise Agreement. Contact sales@apprendo.co for details.
            </p>
          </Card>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Subscription and Billing</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Subscription fees are billed in advance</li>
              <li>All fees are non-refundable except as required by law</li>
              <li>You may cancel your subscription at any time</li>
              <li>We reserve the right to modify pricing with notice</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Intellectual Property</h2>
            <p className="text-muted-foreground">
              You must respect all intellectual property rights. Do not upload content you do not have the right to share. Report any violations to our team immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Disclaimers</h2>
            <p className="text-muted-foreground">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Limitation of Liability</h2>
            <p className="text-muted-foreground">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, APPRENDO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Indemnification</h2>
            <p className="text-muted-foreground">
              You agree to indemnify and hold harmless Apprendo from any claims arising from your use of the service, violation of these terms, or infringement of any rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Termination</h2>
            <p className="text-muted-foreground">
              We may terminate or suspend your account immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users or the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">13. Governing Law</h2>
            <p className="text-muted-foreground">
              These Terms shall be governed by the laws of California, United States, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">14. Changes to Terms</h2>
            <p className="text-muted-foreground">
              We reserve the right to modify these terms at any time. Material changes will be notified via email or platform notification. Continued use constitutes acceptance of new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">15. Contact Information</h2>
            <p className="text-muted-foreground">
              For questions about these Terms, contact us at:
            </p>
            <p className="text-muted-foreground mt-2">
              Email: legal@apprendo.co<br />
              Address: Apprendo Inc., San Francisco, CA
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}